import type { ReactNode } from 'react'
import { Navigate } from 'react-router-dom'
import { isEnabled, type FeatureFlag } from '@/config/features'

interface FeatureGateProps {
  flag: FeatureFlag
  children: ReactNode
  /** Rendered when the flag is OFF. Defaults to `null` (renders nothing). */
  fallback?: ReactNode
  /**
   * Optional path to <Navigate> to when the flag is OFF. Use for whole-page
   * route gates so a deep-linked URL doesn't show a blank page. Takes
   * precedence over `fallback` when both are provided.
   */
  redirectTo?: string
}

/**
 * Renders children only when the named feature flag is enabled.
 *
 * Wrap any Phase 2/3 UI entry point — page components, sidebar links,
 * call-to-action buttons.
 *
 *   <FeatureGate flag="VENDOR_ORDER_BOARD" redirectTo="/vendor">
 *     <LiveOrdersPage />
 *   </FeatureGate>
 *
 * Inline usage (no redirect):
 *
 *   <FeatureGate flag="VENDOR_PAYMENTS">
 *     <ScanQRButton />
 *   </FeatureGate>
 */
export function FeatureGate({ flag, children, fallback = null, redirectTo }: FeatureGateProps) {
  if (isEnabled(flag)) return <>{children}</>
  if (redirectTo) return <Navigate to={redirectTo} replace />
  return <>{fallback}</>
}
